/**
 * Shared query-layer helpers for the two patterns that repeat across domain
 * files: user-scoped SELECTs and single-row mutations.
 *
 * `listForUser(table, opts)` — `SELECT <select>.eq('user_id', opts.userId)`
 * plus optional `eq` filters, ordering, limit, range. Returns `data || []`
 * so callers don't re-check for `null`.
 *
 * `insertOne(table, payload)` / `updateOneById(table, id, patch)` — wrap the
 * `.insert(...).select().single()` / `.update(...).eq('id', ...).select().single()`
 * pattern and return the saved row.
 *
 * Example:
 *   import { listForUser, insertOne, updateOneById } from './_helpers'
 *   const prayers = await listForUser('prayers', { userId, orderBy: 'created_at', ascending: false })
 *   const row = await insertOne('prayers', { user_id, content, source })
 *   const updated = await updateOneById('prayers', prayerId, { content: 'new' })
 */
import { supabase } from '@/lib/supabase'

/**
 * Run a user-scoped SELECT with optional extra eq-style filters, ordering,
 * offset, and limit. Always adds `.eq('user_id', opts.userId)`. If the caller
 * needs a filter beyond eq (gte/lt/in/etc.), don't use this helper — keep the
 * query explicit.
 *
 * @param {string} table
 * @param {Object} opts
 * @param {string} opts.userId - required
 * @param {string} [opts.select='*']
 * @param {Object} [opts.filters] - flat { column: value } pairs → eq
 * @param {string} [opts.orderBy] - column name
 * @param {boolean} [opts.ascending=true]
 * @param {number} [opts.limit]
 * @param {number} [opts.offset]
 * @returns {Promise<Array>}
 */
export async function listForUser(table, opts = {}) {
  const { userId, select = '*', filters = {}, orderBy, ascending = true, limit, offset } = opts
  if (!userId) throw new Error('listForUser requires userId')

  let q = supabase.from(table).select(select).eq('user_id', userId)
  for (const [col, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) {
      q = q.eq(col, value)
    }
  }
  if (orderBy) q = q.order(orderBy, { ascending })
  if (typeof offset === 'number' && typeof limit === 'number') {
    q = q.range(offset, offset + limit - 1)
  } else if (typeof limit === 'number') {
    q = q.limit(limit)
  }

  const { data, error } = await q
  if (error) throw error
  return data || []
}

/**
 * Insert one row and return the saved record.
 * @param {string} table
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function insertOne(table, payload) {
  const { data, error } = await supabase.from(table).insert(payload).select().single()
  if (error) throw error
  return data
}

/**
 * Update one row by id and return the updated record.
 * @param {string} table
 * @param {string|number} id
 * @param {Object} patch
 * @returns {Promise<Object>}
 */
export async function updateOneById(table, id, patch) {
  const { data, error } = await supabase.from(table).update(patch).eq('id', id).select().single()
  if (error) throw error
  return data
}

/**
 * Update one or more columns on a profile row by user id. Fire-and-forget —
 * does not return the row. Used by the "last actual use" writers in auth.js
 * and anywhere else that needs a lightweight profile patch without the
 * `.select().single()` round-trip.
 *
 * @param {string} userId
 * @param {Object} patch - column: value pairs (snake_case DB columns)
 */
export async function updateProfileField(userId, patch) {
  const { error } = await supabase.from('profiles').update(patch).eq('id', userId)
  if (error) throw error
}
